import { AxiosResponse } from "axios";
import { axiosInstance } from "./axiosInstance";
import {
	EnrollmentRequestDetailsDTO, EnrollmentRequestListDTO, ExistingStudentDTO, RequestCatalogsDTO, ReviewersDTO
} from "../../models/EnrollmentHistoryDTO";

const base = (campusId: number) => `/admin/campus/${campusId}/enrollmentRequests`;

export interface RequestPayload {
	modeId?: number;
	studentId?: number | null;
	studentData?: any;
	scheduleId?: number | null;
	advisors?: { userId: string; name: string; sharePercentage?: number | null }[];
	notes?: string | null;
	commercialTerms?: string | null;
	payment?: any;
	submit?: boolean;
}

/** Solicitudes de matrícula (Fase 1). */
export class EnrollmentRequestsService {
	filterOptions = async (campusId: number) =>
		axiosInstance.get<any>(`${base(campusId)}/_filterOptions`);
	list = async (campusId: number, criteria: any, perPage: number, page: number): Promise<AxiosResponse<EnrollmentRequestListDTO>> =>
		axiosInstance.get<EnrollmentRequestListDTO>(`${base(campusId)}?perPage=${perPage}&page=${page}&criteria=${encodeURIComponent(JSON.stringify(criteria || {}))}`);

	catalogs = async (campusId: number): Promise<AxiosResponse<RequestCatalogsDTO>> => axiosInstance.get<RequestCatalogsDTO>(`${base(campusId)}/_catalogs`);

	details = async (campusId: number, requestId: number): Promise<AxiosResponse<EnrollmentRequestDetailsDTO>> =>
		axiosInstance.get<EnrollmentRequestDetailsDTO>(`${base(campusId)}/${requestId}/_details`);

	create = async (campusId: number, payload: RequestPayload): Promise<AxiosResponse<EnrollmentRequestDetailsDTO>> =>
		axiosInstance.post<EnrollmentRequestDetailsDTO>(base(campusId), payload);

	update = async (campusId: number, requestId: number, payload: RequestPayload): Promise<AxiosResponse<EnrollmentRequestDetailsDTO>> =>
		axiosInstance.put<EnrollmentRequestDetailsDTO>(`${base(campusId)}/${requestId}`, payload);

	submit = async (campusId: number, requestId: number) => axiosInstance.put<EnrollmentRequestDetailsDTO>(`${base(campusId)}/${requestId}/_submit`, {});
	approve = async (campusId: number, requestId: number) => axiosInstance.put<EnrollmentRequestDetailsDTO>(`${base(campusId)}/${requestId}/_approve`, {});
	/** Texto + imágenes (capturas) en un solo envío multipart. */
	private conArchivos = (campos: { [k: string]: any }, files?: File[]) => {
		const form = new FormData();
		Object.keys(campos).forEach((k) => { if (campos[k] !== undefined && campos[k] !== null) form.append(k, String(campos[k])); });
		(files || []).forEach((f) => form.append("files[]", f, f.name));
		return form;
	};
	private multipart = { headers: { "Content-Type": "multipart/form-data" } };

	returnToSales = async (campusId: number, requestId: number, comment: string, files?: File[]) =>
		axiosInstance.post<EnrollmentRequestDetailsDTO>(`${base(campusId)}/${requestId}/_return`, this.conArchivos({ comment }, files), this.multipart);
	reassign = async (campusId: number, requestId: number, userId: number, comment: string, files?: File[]) =>
		axiosInstance.post<EnrollmentRequestDetailsDTO>(`${base(campusId)}/${requestId}/_reassign`, this.conArchivos({ userId, comment }, files), this.multipart);
	comment = async (campusId: number, requestId: number, body: string, files?: File[]) =>
		axiosInstance.post<EnrollmentRequestDetailsDTO>(`${base(campusId)}/${requestId}/_comments`, this.conArchivos({ body }, files), this.multipart);
	resendToCaja = async (campusId: number, requestId: number, comment: string, files: File[]) =>
		axiosInstance.post<EnrollmentRequestDetailsDTO>(`${base(campusId)}/${requestId}/_resendToCaja`, this.conArchivos({ comment }, files), this.multipart);
	commentAttachment = async (campusId: number, requestId: number, commentId: number, index: number) =>
		axiosInstance.get<Blob>(`${base(campusId)}/${requestId}/_commentAttachment/${commentId}/${index}`, { responseType: "blob" });

	uploadDocument = async (campusId: number, requestId: number, typeId: number, file: File) => {
		const form = new FormData();
		form.append("typeId", String(typeId));
		form.append("file", file);
		return axiosInstance.post<EnrollmentRequestDetailsDTO>(`${base(campusId)}/${requestId}/_documents`, form, { headers: { "Content-Type": "multipart/form-data" } });
	};
	downloadDocument = async (campusId: number, requestId: number, documentId: number) =>
		axiosInstance.get<Blob>(`${base(campusId)}/${requestId}/_documents/${documentId}`, { responseType: "blob" });
	downloadContract = async (campusId: number, requestId: number, contractId: number, signed = false) =>
		axiosInstance.get<Blob>(`${base(campusId)}/${requestId}/_contracts/${contractId}` + (signed ? "?signed=1" : ""), { responseType: "blob" });
	downloadContractVersion = async (campusId: number, requestId: number, contractId: number, versionId: number) =>
		axiosInstance.get<Blob>(`${base(campusId)}/${requestId}/_contracts/${contractId}/_versions/${versionId}`, { responseType: "blob" });
	uploadSignedContract = async (campusId: number, requestId: number, contractId: number, file: File) => {
		const form = new FormData();
		form.append("file", file);
		return axiosInstance.post<EnrollmentRequestDetailsDTO>(`${base(campusId)}/${requestId}/_contracts/${contractId}/_signed`, form, { headers: { "Content-Type": "multipart/form-data" } });
	};
	regenerateContract = async (campusId: number, requestId: number, contractId: number) =>
		axiosInstance.post<EnrollmentRequestDetailsDTO>(`${base(campusId)}/${requestId}/_contracts/${contractId}/_regenerate`);
	deleteDocument = async (campusId: number, requestId: number, documentId: number) =>
		axiosInstance.delete<EnrollmentRequestDetailsDTO>(`${base(campusId)}/${requestId}/_documents/${documentId}`);

	reviewers = async (campusId: number): Promise<AxiosResponse<ReviewersDTO>> => axiosInstance.get<ReviewersDTO>(`${base(campusId)}/_reviewers`);
	saveReviewer = async (campusId: number, userId: number, active: boolean) => axiosInstance.put<ReviewersDTO>(`${base(campusId)}/_reviewers`, { userId, active });
	findStudents = async (campusId: number, q: string): Promise<AxiosResponse<ExistingStudentDTO[]>> =>
		axiosInstance.get<ExistingStudentDTO[]>(`${base(campusId)}/_findStudents?q=${encodeURIComponent(q)}`);
}
